import { useEffect, useState } from "react";
import { useI18n, LANGS } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { AuthDialog } from "./AuthDialog";
import { api } from "../lib/api";
import { trackVisit } from "../lib/visits";
import { Link } from "react-router-dom";
import { UtensilsCrossed, Clock, CalendarOff, User } from "lucide-react";
import { jstToday } from "../lib/jst";

export const StorefrontHeader = ({ active }) => {
  const { t, lang, setLang } = useI18n();
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data));
    trackVisit();
  }, []);

  const today = new Date().getDay();
  const todayHour = settings?.hours?.find((h) => h.day === today);
  const closedToday =
    todayHour?.closed ||
    (settings?.closed_dates || []).includes(jstToday());

  return (
    <header className="bg-white border-b border-gray-200 overflow-x-hidden">
      <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-3 mr-auto min-w-0">
          <img
            src="/logo.png"
            alt="Kumamoto Halal Food"
            className="h-11 w-11 shrink-0 rounded-xl object-cover"
            data-testid="store-logo"
          />
          <div className="min-w-0">
            <p className="font-display font-semibold leading-tight break-words" data-testid="store-header-name">
              {settings?.store_name || "Kumamoto Halal Food"}
            </p>
            <p className="text-xs text-muted-foreground break-words" data-testid="store-header-meta">
              {settings?.address || "Kumamoto shi, Chuoku, Kurokami 5-5-10"}
              {settings?.phone ? ` · ${settings.phone}` : ""}
            </p>
          </div>
        </div>
        <nav className="flex flex-wrap gap-2">
          <Link
            to="/toko"
            data-testid="store-nav-toko"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
              active === "toko"
                ? "bg-gray-900 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {t("go_store")}
          </Link>
          <Link
            to="/resto"
            data-testid="store-nav-resto"
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
              active === "resto"
                ? "bg-gray-900 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            <UtensilsCrossed size={14} className="inline mr-1" />
            {t("go_resto")}
          </Link>
          <Link
            to="/info"
            data-testid="store-nav-info"
            className={`px-4 py-2 rounded-full text-sm font-medium ${
              active === "info" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {t("store_info")}
          </Link>
          <Link
            to="/kasir"
            data-testid="store-nav-owner"
            className="px-4 py-2 rounded-full text-sm font-medium bg-white border border-gray-200 hover:bg-gray-50"
          >
            {t("owner_area")}
          </Link>
          {user ? (
            <Link
              to="/akun"
              data-testid="store-nav-akun"
              className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 ${
                active === "akun" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <User size={14} /> {user.name}
            </Link>
          ) : (
            <button
              data-testid="store-login-btn"
              onClick={() => setAuthOpen(true)}
              className="px-4 py-2 rounded-full text-sm font-medium bg-primary text-white hover:opacity-90 flex items-center gap-1.5"
            >
              <User size={14} /> {t("login")}
            </button>
          )}
        </nav>
        <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
        <div className="flex gap-1">
          {LANGS.map((l) => (
            <button
              key={l.id}
              data-testid={`store-lang-${l.id}`}
              onClick={() => setLang(l.id)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold ${
                lang === l.id ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>
      {settings && (
        <div
          className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 pb-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm"
          data-testid="store-hours-banner"
        >
          <span className="flex items-center gap-2 text-gray-700">
            <Clock size={15} className="text-primary" />
            {closedToday ? (
              <span className="text-primary font-medium">{t("closed_today")}</span>
            ) : (
              <span>
                {t("open_today")}: {todayHour?.open}–{todayHour?.close}
              </span>
            )}
          </span>
          {settings.holiday_notice && (
            <span
              className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1"
              data-testid="holiday-notice"
            >
              <CalendarOff size={14} />{" "}
              {(lang === "ja" && settings.holiday_notice_ja) || settings.holiday_notice}
            </span>
          )}
        </div>
      )}
    </header>
  );
};
